import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AdminSettingsService } from '../../services/admin-settings.service';
import { GlobalConfig } from '../../models/global-config.model';
import { GLOBAL_CONFIG_KEYS } from '../../shared/constants/global-config-keys.constants';
import { FunctionalLocationHierarchyService } from '../../services/functional-location-hierarchy.service';
import { PlantWithSectors } from '../../models/plant-with-sectors.model';
import { WorkOrderService } from '../../services/work-order.service';
import { SimilarWorkOrderService } from '../../services/similar-work-order.service';
import { SapDataService } from '../../services/sap-data.service';
import { ClearUserSelectedDataRequest } from '../../models/requests/clear-user-selected-data-request';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  standalone: false,
})
export class SettingsComponent implements OnInit {
  configs: GlobalConfig[] = [];
  isLoading = true;
  savingKey: string | null = null;

  editValues: Record<string, number> = {};

  readonly GLOBAL_CONFIG_KEYS = GLOBAL_CONFIG_KEYS;

  plantsWithSectors: PlantWithSectors[] = [];
  isClearingDuplicates = false;
  isClearingPriorities = false;
  isDeletingSapData = false;

  constructor(
    private adminSettingsService: AdminSettingsService,
    private messageService: MessageService,
    private functionalLocationHierarchyService: FunctionalLocationHierarchyService,
    private workOrderService: WorkOrderService,
    private similarWorkOrderService: SimilarWorkOrderService,
    private sapDataService: SapDataService,
  ) {}

  ngOnInit(): void {
    this.loadConfigs();
    this.loadPlantsWithSectors();
  }

  private loadConfigs(): void {
    this.isLoading = true;
    this.adminSettingsService.getAll().subscribe({
      next: (configs) => {
        this.configs = configs;
        configs.forEach((c) => {
          if (c.ValueType === 'double' || c.ValueType === 'float') {
            this.editValues[c.ConfigName] = parseFloat(c.ConfigValue);
          }
        });
        this.isLoading = false;
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les paramètres.',
          life: 4000,
        });
        this.isLoading = false;
      },
    });
  }

  private loadPlantsWithSectors(): void {
    this.functionalLocationHierarchyService
      .getPlantsWithMainWorkCenters()
      .subscribe({
        next: (data) => {
          this.plantsWithSectors = data.Plants;
        },
        error: (err) =>
          console.error('Error loading plants with main work centers:', err),
      });
  }

  save(config: GlobalConfig): void {
    const newValue = this.editValues[config.ConfigName];
    if (newValue === undefined || newValue === null) return;

    this.savingKey = config.ConfigName;
    this.adminSettingsService
      .update(config.ConfigName, newValue.toString())
      .subscribe({
        next: (updated) => {
          const idx = this.configs.findIndex(
            (c) => c.ConfigName === updated.ConfigName,
          );
          if (idx !== -1) this.configs[idx] = updated;
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Sauvegardé',
            detail: `Paramètre "${config.ConfigName}" mis à jour.`,
            life: 3000,
          });
          this.savingKey = null;
        },
        error: () => {
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: `Impossible de mettre à jour "${config.ConfigName}".`,
            life: 4000,
          });
          this.savingKey = null;
        },
      });
  }

  onClearDuplicates(request: ClearUserSelectedDataRequest): void {
    this.isClearingDuplicates = true;
    this.similarWorkOrderService.clearSelections(request).subscribe({
      next: (result) => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Nettoyage terminé',
          detail: `${result.AffectedCount} doublon(s) effacé(s).`,
          life: 4000,
        });
        this.isClearingDuplicates = false;
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: "Impossible d'effacer les doublons sélectionnés.",
          life: 4000,
        });
        this.isClearingDuplicates = false;
      },
    });
  }

  onClearPriorities(request: ClearUserSelectedDataRequest): void {
    this.isClearingPriorities = true;
    this.workOrderService.clearSelectedPriorities(request).subscribe({
      next: (result) => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Nettoyage terminé',
          detail: `${result.AffectedCount} priorité(s) effacée(s).`,
          life: 4000,
        });
        this.isClearingPriorities = false;
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: "Impossible d'effacer les priorités sélectionnées.",
          life: 4000,
        });
        this.isClearingPriorities = false;
      },
    });
  }

  onDeleteSapData(request: ClearUserSelectedDataRequest): void {
    this.isDeletingSapData = true;
    this.sapDataService.deleteByFilter(request).subscribe({
      next: (result) => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'success',
          summary: 'Suppression terminée',
          detail:
            `${result.WorkOrdersDeleted} ordre(s) de travail, ` +
            `${result.WorkNotificationsDeleted} avis de travaux, ` +
            `${result.WorkOrderOperationsDeleted} opération(s) supprimé(s).`,
          life: 6000,
        });
        this.isDeletingSapData = false;
      },
      error: () => {
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de supprimer les données SAP.',
          life: 4000,
        });
        this.isDeletingSapData = false;
      },
    });
  }
}
